import React, { useState } from "react";
import { fallbackCopyTextToClipboard } from "components/utils";
import { ClipboardIcon, CheckIcon } from "@heroicons/react/24/outline";

export const SettingsSectionHeader = ({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) => {
  return (
    <div className="flex lg:flex-row flex-col lg:justify-between lg:items-center gap-4">
      <div className="flex flex-col justify-between items-start">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          {title}
        </h3>
        {description && (
          <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-secondary">
            {description}
          </p>
        )}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
};

export const SettingsSectionContent = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <div className="mt-4">{children}</div>;
};

export const SettingsSectionItems = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <dl className="divide-y divide-gray-200 dark:divide-zinc-800 space-y-2 sm:space-y-3">
      {children}
    </dl>
  );
};

export const SettingsSectionItem = ({
  label,
  displayValue,
  value,
  showCopyButton,
}: {
  label: string;
  displayValue?: string;
  value: string | undefined;
  showCopyButton?: boolean;
}) => {
  const [copied, setCopied] = useState(false);

  return (
    <div className="sm:grid pt-2 sm:pt-3 first:pt-2 sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-gray-900 dark:text-white">
        {label}
      </dt>
      <dd className="mt-1 flex items-center gap-2 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">
        <span>
          {displayValue ?? value ?? (
            <span className="text-secondary">None</span>
          )}
        </span>
        {showCopyButton && (
          <button
            className="text-secondary hover:text-white focus:outline-none"
            onClick={() => {
              fallbackCopyTextToClipboard(value ?? "");
              setCopied(true);
              setTimeout(() => setCopied(false), 1000);
            }}
            title="Copy to clipboard"
          >
            {copied ? (
              <span className="flex items-center gap-1 text-highlight">
                <CheckIcon className="h-4 w-4" />
                <span className="text-xs">Copied</span>
              </span>
            ) : (
              <ClipboardIcon className="h-4 w-4" />
            )}
          </button>
        )}
      </dd>
    </div>
  );
};

export const SettingsSection = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <div className="w-full mt-12 first:mt-0 last:mb-0">{children}</div>;
};
